import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  resolveBriefTemplateActions,
  usePromoteBriefTemplate,
  useTurnOffBriefTemplate,
  useTurnOnBriefTemplate,
  type BriefTemplateActions,
} from '@/entities/brief-template';
import {
  useDeleteBriefTemplate,
  useDuplicateBriefTemplate,
} from '@/entities/brief-template/api/mutations';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import type { BriefTemplatesActionMenuProps } from '../../BriefTemplatesActionMenu.types';

const NO_ACTIONS: BriefTemplateActions = {
  canEdit: false,
  canDelete: false,
  canDuplicate: false,
  canPromote: false,
  canToggle: false,
};

export function useBriefTemplatesActionMenu(props: BriefTemplatesActionMenuProps) {
  const { templateId, level = 'workspace', enabled = true, levelsEnabled = false } = props;
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const [isTurnOffOpen, setIsTurnOffOpen] = useState(false);

  const duplicateMutation = useDuplicateBriefTemplate(templateId);
  const deleteMutation = useDeleteBriefTemplate(templateId);
  const promoteMutation = usePromoteBriefTemplate(templateId);
  const turnOffMutation = useTurnOffBriefTemplate(templateId);
  const turnOnMutation = useTurnOnBriefTemplate(templateId);

  const legacyActions: BriefTemplateActions = permissions.canManageBriefTemplates
    ? { ...NO_ACTIONS, canEdit: true, canDuplicate: true, canDelete: true }
    : NO_ACTIONS;
  const actions = levelsEnabled ? resolveBriefTemplateActions(level, permissions) : legacyActions;
  const hasActions = Object.values(actions).some(Boolean);

  const toggleMutation = enabled ? turnOffMutation : turnOnMutation;
  // With the system key the switch is the template's own, so off reaches every organization.
  // An organization manager's turn-off is their organization's alone and undoes with Turn on.
  const turnOffReachesEveryOrganization =
    levelsEnabled && enabled && level === 'system' && permissions.canManageSystemBriefTemplates;

  return {
    actions,
    hasActions,
    enabled,
    isDeleteOpen,
    isPromoteOpen,
    isTurnOffOpen,
    isDuplicating: duplicateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isPromoting: promoteMutation.isPending,
    isToggling: toggleMutation.isPending,
    handlers: {
      edit: () => void navigate(ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL(templateId)),
      duplicate: () => {
        if (duplicateMutation.isPending) return;
        duplicateMutation.mutate();
      },
      toggle: () => {
        if (toggleMutation.isPending) return;
        if (turnOffReachesEveryOrganization) {
          setIsTurnOffOpen(true);
          return;
        }
        toggleMutation.mutate();
      },
      cancelTurnOff: () => setIsTurnOffOpen(false),
      confirmTurnOff: () =>
        turnOffMutation.mutate(undefined, { onSuccess: () => setIsTurnOffOpen(false) }),
      openDelete: () => setIsDeleteOpen(true),
      cancelDelete: () => setIsDeleteOpen(false),
      confirmDelete: () =>
        deleteMutation.mutate(undefined, { onSuccess: () => setIsDeleteOpen(false) }),
      openPromote: () => setIsPromoteOpen(true),
      cancelPromote: () => setIsPromoteOpen(false),
      confirmPromote: () =>
        promoteMutation.mutate(undefined, { onSuccess: () => setIsPromoteOpen(false) }),
    },
  };
}
